import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { Screen, ScreenHeader } from '../components/chrome/shell'
import { useChrome } from '../app/theme'
import { Button } from '../components/ui/button'
import { Blank, Panel, Row } from '../components/ui/panel'
import { Chip } from '../components/ui/tone'
import { ApiError, api, type OutboundStatus, type SenderIdentity } from '../lib/api'
import { TenantCredentialsPanel } from '../components/settings/tenant-credentials'
import { useSession } from '../lib/session'

/**
 * §83's sender identity — who, in law, is sending the mail.
 *
 * **§30 refuses every send while this is empty**, so an organization with none is one that cannot
 * send at all, and until 2026-08-22 there was nothing anywhere that could set it. The service
 * method existed with no caller: the development machine got its value from a seed script, and the
 * production deployment had none — a system that could never send, for a reason nothing on screen
 * would have explained. Same shape as P039, one week later.
 *
 * The empty state is written as a blocker rather than as an empty field, because that is what it
 * is. The help text says what belongs in it without saying what *this* organization's is: the
 * wording is the tenant's decision and a legal one (P003), and a placeholder that looked like an
 * answer is how a made-up address ends up in somebody's footer.
 */
function SenderIdentityPanel() {
  const { can } = useSession()
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const identity = useQuery({
    queryKey: ['sender-identity'],
    queryFn: () => api.get<SenderIdentity>('/system/sender-identity'),
  })

  const save = useMutation({
    mutationFn: (value: string) => api.post('/system/sender-identity', { value }),
    onSuccess: () => {
      setDraft(null)
      setError(null)
      void queryClient.invalidateQueries({ queryKey: ['sender-identity'] })
    },
    onError: (e: unknown) =>
      setError(e instanceof ApiError ? e.message : 'It could not be saved.'),
  })

  const may = can('pause_system')
  const current = identity.data?.value ?? null
  const editing = draft !== null

  return (
    <Panel
      title="Sender identity"
      description="§83. It appears in the footer of every message this organization sends, and §30 refuses the send without it."
    >
      <Row label="Current">
        {identity.isLoading ? (
          <span className="text-faint">—</span>
        ) : current ? (
          <span className="text-text">{current}</span>
        ) : (
          // Not an empty field. Nothing can send while this is unset, and the screen says so
          // rather than leaving a blank that reads as optional.
          <span className="text-bad">
            not set — nothing can be sent until it is
          </span>
        )}
      </Row>

      {may && (
        <Row label={editing ? 'New value' : 'Change it'}>
          {editing ? (
            <div className="space-y-2">
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                rows={2}
                aria-label="Sender identity"
                className="bg-panel2 border-line focus:border-accent w-full rounded-[7px] border px-2 py-1.5 text-[12.5px] transition-colors focus:outline-none"
              />
              <p className="text-faint m-0 text-[11.5px]">
                The legal name of whoever is sending, and a way to reach them. It has to be true —
                this is the line that identifies the sender to a recipient and to a regulator.
              </p>
              {error && <p className="text-bad m-0 text-[11.5px]">{error}</p>}
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="primary"
                  disabled={save.isPending || draft.trim().length < 12}
                  onClick={() => save.mutate(draft.trim())}
                >
                  Save
                </Button>
                <Button size="sm" onClick={() => { setDraft(null); setError(null) }}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <Button size="sm" onClick={() => setDraft(current ?? '')}>
              {current ? 'Change' : 'Set it'}
            </Button>
          )}
        </Row>
      )}

      {!may && (
        <Row label="Your access">
          <span className="text-muted text-[12px]">
            Changing it needs <code>pause_system</code>. It is not wording — it is what this
            organization asserts about itself in every message.
          </span>
        </Row>
      )}
    </Panel>
  )
}

/**
 * §47's kill switch, made operable.
 *
 * `POST /system/outbound` has existed since the two-tier switch landed and **the console only ever
 * read it** — the topbar and the overview both show whether sending is on, and nothing could turn
 * it off. A stop control that requires a `curl` command is not a stop control; it is a thing you
 * discover you do not have during the incident you needed it for.
 *
 * ### Only the organization tier is here, and that is P027
 *
 * The platform tier stops **every customer at once** and lives behind `platform_pause_all` on the
 * platform plane. No organization role reaches it, however senior — a customer's SUPER_ADMIN
 * halting every other customer's sending is precisely what two planes exist to prevent. The panel
 * *shows* the platform state, because "why can I not send" has to be answerable, and offers no
 * control over it.
 *
 * ### Turning it off is one press; turning it back on asks
 *
 * Deliberately asymmetric. Stopping is the safe direction and an emergency stop that demands a
 * confirmation dialog is not an emergency stop. Resuming is the direction that lets mail reach
 * real people, so it takes a reason, and the reason is audited.
 */
function KillSwitch() {
  const { can } = useSession()
  const queryClient = useQueryClient()
  const [reason, setReason] = useState('')

  const status = useQuery({
    queryKey: ['outbound'],
    queryFn: () => api.get<OutboundStatus>('/system/outbound'),
  })

  const set = useMutation({
    mutationFn: (input: { enabled: boolean; reason: string }) =>
      api.post('/system/outbound', input),
    onSuccess: () => {
      setReason('')
      void queryClient.invalidateQueries({ queryKey: ['outbound'] })
    },
  })

  const may = can('pause_system')
  const platformStopped = status.data ? !status.data.platform : false
  const orgEnabled = status.data?.organization ?? false

  return (
    <Panel title="Outbound sending">
      <Row label="This organization">
        <div className="flex flex-wrap items-center gap-3">
          <Chip tone={orgEnabled ? 'ok' : 'bad'}>
            {orgEnabled ? 'sending' : 'stopped'}
          </Chip>
          {may &&
            (orgEnabled ? (
              <Button
                size="sm"
                variant="danger"
                disabled={set.isPending}
                onClick={() =>
                  set.mutate({
                    enabled: false,
                    reason: reason || 'stopped from the settings screen',
                  })
                }
              >
                Stop sending
              </Button>
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                <input
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Why is it safe to resume?"
                  aria-label="Reason for resuming outbound sending"
                  className="bg-panel2 border-line focus:border-accent w-60 rounded-[7px] border px-2 py-1 text-[12.5px] transition-colors focus:outline-none"
                />
                <Button
                  size="sm"
                  variant="primary"
                  disabled={set.isPending || reason.trim().length < 4}
                  onClick={() => set.mutate({ enabled: true, reason })}
                >
                  Resume sending
                </Button>
              </div>
            ))}
        </div>
      </Row>

      <Row label="Platform">
        <div className="flex items-center gap-3">
          <Chip tone={status.data?.platform ? 'ok' : 'bad'}>
            {status.data?.platform ? 'sending' : 'stopped'}
          </Chip>
          <span className="text-faint text-[11.5px]">
            {platformStopped
              ? 'Axmion has stopped sending for every organization. Nothing this organization does will start it.'
              : 'Axmion-wide. Not changeable from here.'}
          </span>
        </div>
      </Row>

      <Row label="Effective">
        {/* The AND of both tiers, which is the only number that answers "will mail leave". */}
        <Chip tone={status.data?.enabled ? 'ok' : 'bad'}>
          {status.data?.enabled ? 'mail can leave' : 'nothing will send'}
        </Chip>
      </Row>

      {!may && (
        <Row label="Your access">
          <span className="text-muted text-[12px]">
            Stopping and resuming needs <code>pause_system</code>, which your role does not hold.
          </span>
        </Row>
      )}
    </Panel>
  )
}

/**
 * Settings.
 *
 * The design draws organization details, members, invites and a reserved billing slot. Three of
 * those have no API — there is no members endpoint, no invite endpoint and no billing at all, and
 * P027 defers the whole SaaS surface deliberately.
 *
 * So this screen shows what the session already knows, and names what is deferred rather than
 * drawing controls that would fail when pressed. A settings page full of dead switches is the
 * worst version of this screen, not the most complete one.
 */
export function SettingsScreen() {
  const { user, isPlatformStaff } = useSession()
  const { theme, density, toggleTheme, toggleDensity } = useChrome()

  const membership = user?.membership

  return (
    <Screen>
      <ScreenHeader
        title="Settings"
        description="This organization, your access to it, and how this console is displayed."
      />

      <div className="grid gap-4 xl:grid-cols-2">
        <div className="space-y-4">
          <KillSwitch />
          <SenderIdentityPanel />
          <TenantCredentialsPanel />

          <Panel title="Organization">
            {membership ? (
              <>
                <Row label="Name">{membership.organizationName}</Row>
                <Row label="Identifier">
                  <span className="font-mono text-[12px]">{membership.organizationSlug}</span>
                </Row>
                <Row label="Your role">{membership.role.toLowerCase().split('_').join(' ')}</Row>
              </>
            ) : (
              <Blank
                icon="denied"
                title="You belong to no organization"
                body="Endpoints that touch customer data will refuse rather than guess which organization you meant."
              />
            )}
          </Panel>

          <Panel
            title="What you may do here"
            description="Resolved from your membership on every request, never from your session token."
          >
            <div className="flex flex-wrap gap-1.5 px-4 py-3.5">
              {membership && membership.permissions.length > 0 ? (
                membership.permissions.map((permission) => (
                  <Chip key={permission} tone="neutral">
                    <span className="font-mono">{permission}</span>
                  </Chip>
                ))
              ) : (
                <span className="text-faint text-[12px]">No permissions.</span>
              )}
            </div>
          </Panel>

          {isPlatformStaff && (
            <Panel
              title="Axmion staff"
              description="A separate plane. This grants nothing inside any customer's organization, and an organization role grants nothing here (P027)."
            >
              <Row label="Platform role">{user?.platformRole.toLowerCase()}</Row>
              <Row label="Platform permissions">
                {user && user.platformPermissions.length > 0 ? (
                  <span className="flex flex-wrap gap-1.5">
                    {user.platformPermissions.map((permission) => (
                      <Chip key={permission} tone="info">
                        <span className="font-mono">{permission}</span>
                      </Chip>
                    ))}
                  </span>
                ) : (
                  'none'
                )}
              </Row>
            </Panel>
          )}
        </div>

        <div className="space-y-4">
          <Panel title="Display" description="Stored in this browser, not on your account.">
            <Row label="Theme">
              <span className="flex items-center justify-between gap-3">
                <span>{theme === 'dark' ? 'Dark' : 'Light'}</span>
                <Button size="sm" onClick={toggleTheme}>
                  Switch to {theme === 'dark' ? 'light' : 'dark'}
                </Button>
              </span>
            </Row>
            <Row label="Row density">
              <span className="flex items-center justify-between gap-3">
                <span>{density === 'dense' ? 'Dense' : 'Comfortable'}</span>
                <Button size="sm" onClick={toggleDensity}>
                  Switch to {density === 'dense' ? 'comfortable' : 'dense'}
                </Button>
              </span>
            </Row>
          </Panel>

          <Panel title="You">
            <Row label="Name">{user?.name}</Row>
            <Row label="Email">
              <span className="font-mono text-[12px]">{user?.email}</span>
            </Row>
            <Row label="Organizations">
              {user && user.memberships.length > 1
                ? `${user.memberships.length} — no switcher yet`
                : '1'}
            </Row>
          </Panel>

          <Panel title="Deferred, deliberately">
            <div className="px-4 py-3.5">
              <p className="text-muted text-[12.5px] text-pretty">
                Members and invitations, billing and plans, self-serve signup and the organization
                switcher are all designed and none are built. P027's rule was to make the structure
                multi-tenant from day one and defer the SaaS surface — these are that surface.
              </p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {['Members', 'Invitations', 'Billing', 'Plans', 'Signup', 'Org switcher'].map(
                  (label) => (
                    <Chip key={label}>{label}</Chip>
                  ),
                )}
              </div>
            </div>
          </Panel>
        </div>
      </div>
    </Screen>
  )
}
